import {ChangeDetectionStrategy, Component, computed, signal} from "@angular/core"
import {BaseInspectorComponent} from "@template-editor/components/inspectors/base-inspector/base-inspector.component"
import {InspectorSectionComponent} from "@template-editor/components/inspectors/inspector-section/inspector-section.component"
import {ValueSlotComponent, SelectionPossibilities} from "@template-editor/components/value-slot/value-slot.component"
import {isValidExternalId, getTemplateNodeClassLabel} from "@cm/template-nodes"
import {
    findClosestParentComponentGraph,
    collectMattressNodes,
    MattressCurveExport,
    MattressReferenceFrameExport,
    isMattressCurveLikeNode,
    isMattressReferenceFrameLikeNode,
} from "@cm/mattress-nodes"
import type {MattressCurveLikeNode, MattressReferenceFrameLikeNode, MattressExport} from "@cm/mattress-nodes"

@Component({
    selector: "cm-mattress-export-inspector",
    templateUrl: "./mattress-export-inspector.component.html",
    styleUrl: "./mattress-export-inspector.component.scss",
    imports: [InspectorSectionComponent, ValueSlotComponent],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MattressExportInspectorComponent extends BaseInspectorComponent<MattressExport> {
    readonly $class = computed(() => getTemplateNodeClassLabel(this.$node()).replace("Export", "Output"))
    readonly $isValidExternalId = signal(isValidExternalId)

    readonly $curveExport = computed(() => {
        const node = this.$node()
        return node instanceof MattressCurveExport ? node : null
    })

    readonly $curveExportSelectionPossibilities = computed<SelectionPossibilities<MattressCurveLikeNode | null>>(() => {
        const node = this.$curveExport()
        if (!node) return []
        const parentGraph = findClosestParentComponentGraph(node)
        const availableCurves = Array.from(collectMattressNodes(parentGraph, isMattressCurveLikeNode))
        return [{name: "None", value: null}, ...availableCurves.map((curve) => ({name: curve.parameters.name, value: curve}))]
    })

    readonly $referenceFrameExport = computed(() => {
        const node = this.$node()
        return node instanceof MattressReferenceFrameExport ? node : null
    })

    readonly $referenceFrameExportSelectionPossibilities = computed<SelectionPossibilities<MattressReferenceFrameLikeNode | null>>(() => {
        const node = this.$referenceFrameExport()
        if (!node) return []
        const parentGraph = findClosestParentComponentGraph(node)
        const availableReferenceFrames = Array.from(collectMattressNodes(parentGraph, isMattressReferenceFrameLikeNode))
        return [{name: "None", value: null}, ...availableReferenceFrames.map((rf) => ({name: rf.parameters.name, value: rf}))]
    })
}
